import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Check, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

// Define a custom event type
declare global {
  interface WindowEventMap {
    'openCookieConsent': CustomEvent;
  }
}

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true and can't be disabled
    analytics: false,
    marketing: false,
  });

  // Check localStorage on mount to see if user has already set preferences
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookieConsent');
    
    if (!savedPreferences) {
      // If no preferences found, show the banner
      setIsOpen(true);
    } else {
      // If preferences found, parse and set them
      try {
        const parsedPreferences = JSON.parse(savedPreferences);
        setPreferences(parsedPreferences);
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
        setIsOpen(true);
      }
    }
  }, []);

  // Listen for the custom event to open the cookie consent banner
  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openCookieConsent', handleOpen);
    
    return () => {
      window.removeEventListener('openCookieConsent', handleOpen);
    };
  }, []);

  const savePreferences = (newPreferences: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    setIsOpen(false);
    setShowCustomize(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(essentialOnly);
  };

  const handleCustomizeClick = () => {
    setShowCustomize(true);
  };

  const handleSaveCustomized = () => {
    savePreferences(preferences);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-2xl p-6 text-slate-900 w-full max-w-md z-10 relative">
            {!showCustomize ? (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <Cookie className="w-6 h-6 text-sky-600 mr-2" />
                    <h3 className="text-xl font-semibold">{t('cookies.cookieConsent.title')}</h3>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-slate-500 hover:text-slate-900 focus:outline-none"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-600 mb-5">{t('cookies.cookieConsent.description')}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={handleRejectNonEssential}
                    className="px-4 py-2 bg-white border border-slate-300 hover:border-slate-400 text-slate-900 rounded-md transition-colors text-sm"
                  >
                    {t('cookies.cookieConsent.buttons.rejectAll')}
                  </button>
                  <button
                    onClick={handleCustomizeClick}
                    className="px-4 py-2 bg-white border border-slate-300 hover:border-slate-400 text-slate-900 rounded-md transition-colors text-sm flex items-center justify-center"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    {t('cookies.cookieConsent.buttons.customize')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md transition-colors text-sm"
                  >
                    {t('cookies.cookieConsent.buttons.acceptAll')}
                  </button>
                </div>
                <div className="mt-4 text-xs text-slate-500 text-center">
                  <Link to="/cookies" className="underline hover:text-sky-700">
                    {t('cookies.title')}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <Settings className="w-6 h-6 text-sky-600 mr-2" />
                    <h3 className="text-xl font-semibold">{t('cookies.cookieConsent.buttons.customize')}</h3>
                  </div>
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="text-slate-500 hover:text-slate-900 focus:outline-none"
                    aria-label="Back"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-5">
                  {/* Necessary cookies */}
                  <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className={`w-5 h-5 flex items-center justify-center rounded bg-sky-600 text-white`}>
                          <Check className="w-3 h-3" />
                        </div>
                        <span className="ml-2 font-medium">{t('cookies.cookieConsent.categories.necessary.title')}</span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-sky-100 text-sky-700 rounded-full">
                        {t('cookies.cookieConsent.categories.necessary.title')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {t('cookies.cookieConsent.categories.necessary.description')}
                    </p>
                  </div>
                  
                  {/* Analytics cookies */}
                  <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <button
                          onClick={() => handlePreferenceChange('analytics')}
                          className={`w-5 h-5 flex items-center justify-center rounded ${preferences.analytics ? 'bg-sky-600 text-white' : 'bg-slate-300'}`}
                        >
                          {preferences.analytics && <Check className="w-3 h-3" />}
                        </button>
                        <span className="ml-2 font-medium">{t('cookies.cookieConsent.categories.analytics.title')}</span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded-full">
                        Optional
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {t('cookies.cookieConsent.categories.analytics.description')}
                    </p>
                  </div>
                  
                  {/* Marketing cookies */}
                  <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <button
                          onClick={() => handlePreferenceChange('marketing')}
                          className={`w-5 h-5 flex items-center justify-center rounded ${preferences.marketing ? 'bg-sky-600 text-white' : 'bg-slate-300'}`}
                        >
                          {preferences.marketing && <Check className="w-3 h-3" />}
                        </button>
                        <span className="ml-2 font-medium">{t('cookies.cookieConsent.categories.marketing.title')}</span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded-full">
                        Optional
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {t('cookies.cookieConsent.categories.marketing.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveCustomized}
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md transition-colors"
                  >
                    {t('cookies.cookieConsent.save')}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 